import { type ChangeEvent, forwardRef } from 'react';

import { useBehavioralTrackingProps } from '../../core/hooks/useBehavioralTrackingProps.js';
import { useControllableState } from '../../core/hooks/useControllableState.js';
import type { BehaviorTrackingProps } from '../../core/types/behavior-tracking-props.js';
import type { FormControlProps } from '../../core/types/form-control-props.js';
import { WithChildren } from '../../core/types/with-children.js';
import { mergeProps } from '../../core/utils/merge-props.js';
import type { BaseInputProps } from '../_base-input/base-input.types.js';
import { BaseInput } from '../_base-input/BaseInput.js';
import { BaseInputButton } from '../_base-input/BaseInputButton.js';
import {
  BaseInputSuffix,
  BaseInputPrefix,
} from '../_base-input/BaseInputContent.js';
import type { FormControlRef } from '../shared-types.js';

const COMPONENT_NAME = 'TextInput';

// #region Typings
/**
 * @deprecated - please use TextInputProps instead.
 * @public
 */
export interface TextInputControlledProps {
  /** The default value of the input. */
  defaultValue?: never;
  /** The value of the input. */
  value?: string;
}

/**
 * @deprecated - please use TextInputProps instead.
 * @public
 */
export interface TextInputUncontrolledProps {
  /** The default value of the input. */
  defaultValue?: string;
  /** The value of the input. */
  value?: never;
}

/**
 * The properties of the TextInput component.
 * @public
 */
export type TextInputProps = Pick<
  BaseInputProps,
  | 'autoComplete'
  | 'placeholder'
  | 'variant'
  | 'minLength'
  | 'maxLength'
  | 'pattern'
  | 'onClick'
  | 'onKeyDown'
  | 'onKeyUp'
  | 'onFocus'
  | 'onBlur'
  | 'role'
  | 'className'
  | 'style'
  | 'data-testid'
  | 'data-dtrum-mask'
  | 'data-dtrum-allow'
> &
  FormControlProps<
    string,
    (value: string, event: ChangeEvent<HTMLInputElement>) => void
  > &
  WithChildren &
  BehaviorTrackingProps & {
    /**
     * Maps to the native input type. Use this to enhance the semantics
     * of the native input, change default keyboards on mobile or support browser
     * autocompletion for specific types.
     * @see {@link https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input#input_types}
     *
     * @defaultValue 'text'
     */
    type?: 'text' | 'password' | 'email' | 'url' | 'tel' | 'search';
  };
// #endregion

const TextInputRoot = /* @__PURE__ */ forwardRef<
  FormControlRef<HTMLDivElement>,
  TextInputProps
>((props, forwardedRef) => {
  const {
    onChange,
    value,
    defaultValue,
    required,
    readOnly,
    type = 'text',
    'data-testid': dataTestId,
    'data-dtrum-mask': dataDtrumMask,
    'data-dtrum-allow': dataDtrumAllow,
    'aria-disabled': ariaDisabledProp,
    ...remainingProps
  }: TextInputProps = props;

  const [behavioralTrackingProps, restProps] =
    useBehavioralTrackingProps(remainingProps);

  const [internalValue, setInternalValue] = useControllableState({
    defaultValue,
    value,
  });

  const handleOnChange = (e: ChangeEvent<HTMLInputElement>) => {
    onChange?.(e.target.value, e);
    setInternalValue(e.target.value);
  };

  return (
    <BaseInput
      ref={forwardedRef}
      {...mergeProps(restProps, behavioralTrackingProps)}
      type={type}
      value={internalValue}
      required={required}
      readOnly={readOnly}
      onChange={handleOnChange}
      data-testid={dataTestId}
      data-dtrum-mask={dataDtrumMask}
      data-dtrum-allow={dataDtrumAllow}
      aria-disabled={ariaDisabledProp}
      data-dt-component={COMPONENT_NAME}
    />
  );
});

/**
 * Use the `TextInput` component to request a small amount of information,
 * such as a name or an email address. To let users input
 * larger amounts of text, use a `TextArea` instead.
 * @public
 */
export const TextInput = Object.assign(TextInputRoot, {
  Prefix: BaseInputPrefix,
  Suffix: BaseInputSuffix,
  Button: BaseInputButton,
});

(TextInput as typeof TextInput & { displayName: string }).displayName =
  COMPONENT_NAME;
